import { useState } from 'react'
import { useNavigate } from 'react-router'
import { observer } from 'mobx-react-lite'
import {
  Box,
  CircularProgress,
  IconButton,
  Avatar,
  Menu,
  MenuItem,
  Divider,
  Typography,
  Select,
  type SelectChangeEvent,
} from '@mui/material'
import {
  KeyboardArrowDown as ArrowDownIcon,
  Logout as LogoutIcon,
  Person as PersonIcon,
  Settings as SettingsIcon,
  Domain as OrgIcon,
  UnfoldMore as SelectorIcon,
  Public as AllOrgsIcon,
} from '@mui/icons-material'
import { authStore } from '../../lib/stores/authStore'
import { useOrgSelector } from '../../context/OrgSelectorContext'
import NotificationBell from '../../components/shared/NotificationBell'
import { ConfirmDialog } from '../../components/shared/ConfirmDialog'
import { GlobalSearch } from '../../components/shared/GlobalSearch'
import { themeColors } from './theme'

// ── Helpers ───────────────────────────────────────────────────────────────────

function formatDisplayName(email: string): string {
  const username = email.split('@')[0]
  return username
    .split(/[._-]/)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ')
}

// ── Org Selector (SuperAdmin only) ────────────────────────────────────────────

const OrgSelectorDropdown = observer(() => {
  const { selectedOrgId, selectedOrgName, orgs, isLoading, setSelectedOrgId } = useOrgSelector()
  const ALL = '__ALL__'

  const handleChange = (e: SelectChangeEvent) => {
    const val = e.target.value
    setSelectedOrgId(val === ALL ? null : val)
  }

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
      <Select
        value={selectedOrgId ?? ALL}
        onChange={handleChange}
        size="small"
        variant="outlined"
        disabled={isLoading}
        startAdornment={
          isLoading
            ? <CircularProgress size={12} sx={{ mr: 0.5, color: themeColors.primary }} />
            : selectedOrgId === null
              ? <AllOrgsIcon sx={{ fontSize: 13, color: themeColors.primary, mr: 0.5 }} />
              : <OrgIcon     sx={{ fontSize: 13, color: themeColors.primary, mr: 0.5 }} />
        }
        IconComponent={SelectorIcon}
        renderValue={() => (
          <Typography
            sx={{
              fontSize: '13px',
              fontWeight: 600,
              color: '#1e293b',
              maxWidth: 160,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {isLoading ? 'Loading…' : selectedOrgName}
          </Typography>
        )}
        sx={{
          height: 36,
          minWidth: 140,
          maxWidth: 220,
          bgcolor: '#FFFFFF',
          borderRadius: '8px',
          border: '1px solid #E5E5E5',
          '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
          '& .MuiSelect-select': { pl: '8px !important', pr: '28px !important', py: '0 !important' },
          '& .MuiSelect-icon': { fontSize: 16, color: '#64748b', right: 6 },
          '&:hover': { border: `1px solid ${themeColors.primary}` },
          '&.Mui-disabled': { bgcolor: '#f3f4f6', opacity: 0.7 },
        }}
      >
        <MenuItem value={ALL} sx={{ fontSize: '13px', gap: 1 }}>
          <AllOrgsIcon sx={{ fontSize: 16, color: themeColors.primary }} />
          <Box>
            <Typography fontSize="13px" fontWeight={600}>All Organizations</Typography>
            <Typography fontSize="11px" color="text.secondary">View data across all orgs</Typography>
          </Box>
        </MenuItem>

        {orgs.length > 0 && <Divider sx={{ my: 0.5 }} />}

        {orgs.map(org => (
          <MenuItem key={org.id} value={org.id} sx={{ fontSize: '13px', gap: 1 }}>
            <Box
              sx={{
                width: 24, height: 24, borderRadius: '6px',
                bgcolor: '#f3f0ff',
                display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
              }}
            >
              <OrgIcon sx={{ fontSize: 13, color: '#5C35D8' }} />
            </Box>
            <Typography fontSize="13px" fontWeight={500} noWrap sx={{ maxWidth: 160 }}>
              {org.name}
            </Typography>
          </MenuItem>
        ))}

        {!isLoading && orgs.length === 0 && (
          <MenuItem disabled sx={{ fontSize: '12px', color: 'text.disabled', fontStyle: 'italic' }}>
            No organizations found
          </MenuItem>
        )}
      </Select>

    </Box>
  )
})

// ── Non-SuperAdmin org badge ──────────────────────────────────────────────────

const OrgBadge = observer(() => {
  const ZERO_GUID = '00000000-0000-0000-0000-000000000000'
  const isRealId = (id?: string | null) => !!id && id !== ZERO_GUID && id !== ''

  const orgOk  = isRealId(authStore.user?.orgId)

  const orgName = orgOk
    ? (authStore.user!.orgName ?? authStore.user!.orgId?.slice(-6).toUpperCase() ?? '?')
    : 'No Tenant'

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        height: 36,
        px: '12px',
        border: '1px solid #E5E5E5',
        borderRadius: '8px',
        backgroundColor: '#FFFFFF',
        boxSizing: 'border-box',
      }}
    >
      <Avatar
        sx={{
          width: 22,
          height: 22,
          fontSize: '10px',
          fontWeight: 700,
          bgcolor: '#5C35D8',
          flexShrink: 0,
        }}
      >
        {orgName.charAt(0).toUpperCase()}
      </Avatar>
      <Typography
        sx={{
          fontSize: '13px',
          fontWeight: 600,
          color: '#1e293b',
          maxWidth: 140,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {orgName}
      </Typography>
    </Box>
  )
})

// ── Main Topbar ───────────────────────────────────────────────────────────────

export const Topbar = observer(() => {
  const [anchorEl,     setAnchorEl]     = useState<null | HTMLElement>(null)
  const [isLogoutConfirmOpen, setIsLogoutConfirmOpen] = useState(false)
  const navigate = useNavigate()
  const { isSuperAdmin } = useOrgSelector()

  const handleLogoutClick = () => {
    setAnchorEl(null)
    setIsLogoutConfirmOpen(true)
  }

  const handleConfirmLogout = async () => {
    setIsLogoutConfirmOpen(false)
    await authStore.logout()
  }

  const email       = authStore.user?.email ?? ''
  const displayName = email ? formatDisplayName(email) : 'User'
  const initials    = authStore.userInitials

  return (
    <Box
      sx={{
        height: 56,
        backgroundColor: '#ffffff',
        borderBottom: '1px solid #ebebf5',
        display: 'flex',
        alignItems: 'center',
        px: '20px',
        gap: '12px',
        flexShrink: 0,
        zIndex: 100,
      }}
    >
      {/* Global Search */}
      <GlobalSearch />

      {/* Spacer */}
      <Box sx={{ flex: 1 }} />

      {/* Org Selector (SuperAdmin) or Org Badge (regular user) */}
      {authStore.user && (
        isSuperAdmin ? <OrgSelectorDropdown /> : <OrgBadge />
      )}

      {/* Notifications */}
      <NotificationBell />

      {/* Vertical divider */}
      <Box sx={{ width: '1px', height: 32, backgroundColor: '#E5E5E5', flexShrink: 0 }} />

      {/* User Section */}
      <Box
        onClick={(e) => setAnchorEl(e.currentTarget)}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          cursor: 'pointer',
          px: '8px',
          py: '5px',
          borderRadius: '24px',
          transition: 'background 0.15s',
          '&:hover': { backgroundColor: '#f5f5f9' },
        }}
      >
        <Avatar
          sx={{
            width: 34,
            height: 34,
            fontSize: '12px',
            fontWeight: 700,
            background: themeColors.primaryGradient,
          }}
        >
          {initials}
        </Avatar>
        <Box sx={{ display: 'flex', flexDirection: 'column', lineHeight: 1 }}>
          <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#1e293b', lineHeight: 1.3 }}>
            {displayName}
          </Typography>
          <Typography sx={{ fontSize: '11.5px', color: '#94a3b8', lineHeight: 1.3 }}>
            {email}
          </Typography>
        </Box>
        <ArrowDownIcon sx={{ fontSize: 16, color: '#94a3b8', flexShrink: 0 }} />
      </Box>

      {/* User Dropdown Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        slotProps={{
          paper: {
            sx: {
              mt: 1,
              borderRadius: '10px',
              boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
              minWidth: 210,
              border: '1px solid #e8e8f0',
            },
          },
        }}
      >
        <Box sx={{ px: 2, py: 1.5 }}>
          <Typography sx={{ fontWeight: 600, fontSize: '14px', color: '#1e293b' }}>
            {displayName}
          </Typography>
          <Typography sx={{ fontSize: '12px', color: '#94a3b8' }}>{email}</Typography>
          {authStore.user?.roleName && (
            <Typography
              sx={{
                fontSize: '11px',
                color: themeColors.primary,
                fontWeight: 600,
                mt: 0.3,
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
              }}
            >
              {authStore.user.roleName}
            </Typography>
          )}
        </Box>
        <Divider sx={{ borderColor: '#ebebf5' }} />
        {/* <MenuItem
          onClick={() => { setAnchorEl(null); navigate('/profile') }}
          sx={{ gap: 1.5, fontSize: '13px', py: 1, mx: '4px', borderRadius: '6px', '&:hover': { color: themeColors.primary } }}
        >
          <PersonIcon sx={{ fontSize: 18 }} /> My Profile
        </MenuItem> */}
        <MenuItem
          onClick={() => { setAnchorEl(null); navigate('/settings') }}
          sx={{ gap: 1.5, fontSize: '13px', py: 1, mx: '4px', borderRadius: '6px', '&:hover': { color: themeColors.primary } }}
        >
          <SettingsIcon sx={{ fontSize: 18 }} /> Settings
        </MenuItem>
        <Divider sx={{ borderColor: '#ebebf5' }} />
        <MenuItem
          onClick={handleLogoutClick}
          sx={{ gap: 1.5, fontSize: '13px', py: 1, mx: '4px', borderRadius: '6px', color: '#ef4444' }}
        >
          <LogoutIcon sx={{ fontSize: 18 }} /> Log Out
        </MenuItem>
      </Menu>

      <ConfirmDialog
        open={isLogoutConfirmOpen}
        title="Log Out"
        description="Are you sure you want to log out?"
        confirmLabel="Log Out"
        confirmColor="error"
        onClose={() => setIsLogoutConfirmOpen(false)}
        onConfirm={handleConfirmLogout}
      />
    </Box>
  )
})
